import React, { createContext, useContext, ReactNode, useMemo, useCallback, useEffect, useState } from 'react';
import { EntityRecord, WidgetParamKeys } from '@/types/entity';
import { useSwivuNotes, TUseSwivuNotesReturn } from '@/react/hooks/entityHooks/useSwivuNotes';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';
import { EDITOR_KEYS } from '@/other/constants/EditorKeys';
import { useSwivuStore } from '@/react/hooks/useSwivuStore';
import { BoardNote } from '@/bryntum-lib/models/BoardNote';
import { STORE_KEYS } from '@/other/constants/StoreKeys';
import { useSwivuRichEditor, UseSwivuRichEditorReturn } from '@/react/hooks/useSwivuRichEditor';

interface EntityNotesContextValue extends TUseSwivuNotesReturn, UseSwivuRichEditorReturn {
  entityRecord: EntityRecord;
  handleOpenEditor: (noteRecord?: BoardNote, isCreatingNew?: boolean) => void;
  selectedNote: SelectedNote;
  handleSelectNote: (noteRecord: BoardNote) => Promise<void>;
  handleClearSelectedNote: () => void;
  isSelectedNote: boolean;
  canEditSelectedNote: boolean;
  newNote: BoardNote | null;
  onAfterSaveNote: () => void;
  handleCloseEditor: () => void;
}

const EntityNotesContext = createContext<EntityNotesContextValue | undefined>(undefined);

interface EntityNotesProviderProps {
  richEditorHeight?: number | string;
  entityRecord: EntityRecord;
  initialNoteRecord?: BoardNote;
  isCreatingNew?: boolean;
  children: ReactNode;
  onCloseEditor?: () => void;
}

interface SelectedNote {
  noteRecord: BoardNote | null;
  noteContent?: string;
}

export const EntityNotesProvider: React.FC<EntityNotesProviderProps> = ({ children, entityRecord, initialNoteRecord, isCreatingNew = false, richEditorHeight = "100%", onCloseEditor }) => {
  const notesOperations = useSwivuNotes({ entityRecord });
  const { editorHandler, user, entityManager } = useSwivuBryntumContext();
  const { callParamAction } = entityManager;
  const { subscribe: subscribeToNotesStore, version } = useSwivuStore<BoardNote>({ storeId: STORE_KEYS.boardNotesStore });
  const [selectedNote, setSelectedNote] = useState<SelectedNote>({ noteRecord: initialNoteRecord || null })
  const [newNote, setNewNote] = useState<BoardNote | null>(null);
  const isSelectedNote = Boolean(selectedNote.noteRecord);
  const swivuRichEditor = useSwivuRichEditor({height: richEditorHeight});

  const fetchAndUpdateNoteContent = useCallback(async (noteRecord: BoardNote) => {
    const noteContent = await notesOperations.getNoteData(noteRecord.id) || "";
    setSelectedNote((prev) => ({ ...prev, noteContent }));
  }, [notesOperations.getNoteData, setSelectedNote])

  const handleSelectNote = useCallback(async (noteRecord: BoardNote) => {
    setSelectedNote((prev) => ({ ...prev, noteRecord }));
    await fetchAndUpdateNoteContent(noteRecord);
  }, [notesOperations.getNoteData, setSelectedNote])

  const handleClearSelectedNote = useCallback(() => {
    setSelectedNote(() => ({ noteRecord: null }));
  }, [setSelectedNote])

  const handleCloseEditor = useCallback(() => {
    onCloseEditor?.();
  }, [onCloseEditor])

  const onAfterSaveNote = useCallback(() => {
    if (newNote) {
      setSelectedNote({ noteRecord: newNote });
      setNewNote(null);
    }
  }, [newNote]);

  /* Handle open editor */
  const handleOpenEditor = useCallback((noteRecord?: BoardNote, isCreatingNew?: boolean) => {
    editorHandler.openEditor(
      EDITOR_KEYS.noteEditor,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      null,
      {
        entityRecord,
        noteRecord,
        isCreatingNew,
      },
      { by: "recordType", useExisting: true }
    );
  }, [editorHandler, entityRecord]);

  useEffect(() => {
    if (initialNoteRecord) {
      handleSelectNote(initialNoteRecord);
    }
  }, [initialNoteRecord])

  useEffect(() => {
    if (notesOperations.notes.length === 1 && !isCreatingNew) {
      handleSelectNote(notesOperations.notes[0]);
    }
  }, [notesOperations.notes, handleSelectNote, isCreatingNew]);

  useEffect(() => {
    if (isCreatingNew) {
      const newNoteRecord = notesOperations.createNewNote(entityRecord);
      setNewNote(newNoteRecord);
    }
  }, [isCreatingNew, entityRecord, notesOperations.createNewNote, setNewNote]);

  /** Handle store updates */
  useEffect(() => {
    return subscribeToNotesStore(({ record: changedRecord, action }) => {
      if (changedRecord[entityRecord.entityType] === entityRecord.id) {
        notesOperations.refreshNotes();

        /* If the note that was changed is 'selected' either in editor or in preview */
        if (changedRecord.id === selectedNote.noteRecord?.id) {
          const canView = notesOperations.canViewNote(changedRecord);
          if (!swivuRichEditor.hasChanges) {
            if (action === "remove") {
              handleClearSelectedNote();
              handleCloseEditor();
            } else if (canView) {
              fetchAndUpdateNoteContent(changedRecord);
            }
          }

          if (!canView) {
            handleClearSelectedNote();
            handleCloseEditor();
          }
        }
      }
      return true;
    });
  }, [subscribeToNotesStore, notesOperations.refreshNotes, swivuRichEditor.hasChanges, entityRecord, selectedNote.noteRecord, notesOperations.canViewNote, handleClearSelectedNote, handleCloseEditor]);

  /* Handle deeplinking */
  useEffect(() => {
    callParamAction(WidgetParamKeys.NOTE.EDIT, (id) => {
      if (!notesOperations.notes.length) return false;
      const noteRecord = notesOperations.notes.find((note) => note.id === id);
      if (noteRecord) {
        handleSelectNote(noteRecord);
        handleOpenEditor(noteRecord);
      }
      return true;
    });
  }, [callParamAction, notesOperations.notes])

  const canEditSelectedNote = useMemo(() => {
    if (!selectedNote.noteRecord || !user) return false;
    return notesOperations.canEditNote(selectedNote.noteRecord);
  }, [selectedNote.noteRecord, user, version])

  const contextValue = useMemo(
    () => ({
      ...notesOperations,
      ...swivuRichEditor,
      entityRecord,
      handleOpenEditor,
      selectedNote,
      handleSelectNote,
      isSelectedNote,
      handleClearSelectedNote,
      canEditSelectedNote,
      newNote,
      onAfterSaveNote,
      handleCloseEditor
    }),
    [notesOperations, swivuRichEditor, entityRecord, handleOpenEditor, selectedNote, handleSelectNote, handleClearSelectedNote, isSelectedNote, canEditSelectedNote, newNote, onAfterSaveNote, handleCloseEditor]
  );

  return <EntityNotesContext.Provider value={contextValue}>{children}</EntityNotesContext.Provider>;
};

export const useEntityNotesContext = (): EntityNotesContextValue => {
  const context = useContext(EntityNotesContext);
  if (!context) {
    throw new Error('useEntityNotesContext must be used within EntityNotesProvider');
  }
  return context;
};

export type { EntityNotesContextValue };